import React, { useEffect, useState } from 'react';
import { useTagAndSegment } from '../../contexts/tagAndSegment';
import { useNavigate } from 'react-router-dom';
import Modal from '../Modal';
import { useModal } from '../../contexts/modal';
import SegmentListRow from '../SegmentListRow';
import service from '../../service';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import LoadingSpinner from '../LoadingSpinner';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import ShowFor from '@Components/ShowFor';

const SegmentList = () => {
  const { readAllSegments, segmentArray, setSegmentArray } = useTagAndSegment();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();

  const [editingData, setEditingData] = useState({
    id: null,
    name: '',
  });

  const [selectedSegmentIds, setSelectedSegmentIds] = useState([]);

  const [segmentsLoaded, setSegmentsLoaded] = useState(false);

  useEffect(() => {
    const init = async () => {
      await readAllSegments();
      setSegmentsLoaded(true);
    };
    init();
  }, []);

  useEffect(() => {
    if (!segmentsLoaded || !segmentArray.length) return;

    const fetchCounts = async () => {
      const updated = await Promise.all(
        segmentArray.map(async (s) => {
          const data = await service.getFishForSegmentConditions(s.conditions);
          const count = data?.length || 0;
          return { ...s, count };
        }),
      );
      setSegmentArray(updated);
    };

    fetchCounts();
  }, [segmentsLoaded]);

  const handleCellClick = (segmentId) => {
    if (!editingData.id) {
      navigate(`/segments/${segmentId}`);
    }
  };

  const handleEditClick = (segment) => {
    setEditingData({
      id: segment.id,
      name: segment.name || '',
    });
  };

  const handleInputChange = (e, field) => {
    setEditingData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSaveClick = async () => {
    const segment = segmentArray.find((s) => s.id === editingData.id);
    try {
      await service.updateSegment(editingData.id, {
        name: editingData.name,
        conditions: segment.conditions,
      });
      setEditingData({
        id: null,
        name: '',
      });
      setSegmentArray(
        segmentArray.map((s) =>
          s.id === editingData.id ? { ...s, name: editingData.name } : s,
        ),
      );
      toastSuccess('Segment updated successfully');
    } catch (error) {
      console.error(error);
      toastError(error.message || 'Failed to update segment');
    }
  };

  const handleCancelClick = () => {
    setEditingData({
      id: null,
      name: '',
    });
  };

  const handleCheckboxChange = (fishId) => {
    setSelectedSegmentIds((prev) =>
      prev.includes(fishId)
        ? prev.filter((id) => id !== fishId)
        : [...prev, fishId],
    );
  };

  const isSelected = (fishId) => selectedSegmentIds.includes(fishId);

  const handleSelectAllChange = (e) => {
    if (e.target.checked) {
      const allIds = segmentArray.map((t) => t.id);
      setSelectedSegmentIds(allIds);
    } else {
      setSelectedSegmentIds([]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedSegmentIds.length > 0) {
      openModal();
    }
  };

  const confirmDelete = async () => {
    for (const id of selectedSegmentIds) {
      await service.deleteSegment(id);
    }
    setSelectedSegmentIds([]);
    closeModal();
    setSegmentArray(
      segmentArray.filter((s) => !selectedSegmentIds.includes(s.id)),
    );
  };

  const cancelDelete = () => {
    closeModal();
  };

  const allSelected =
    segmentArray.length > 0 &&
    selectedSegmentIds.length === segmentArray.length;

  return (
    <div className="p-4">
      <ShowFor permissions={['SEGMENTS_WRITE']}>
        <div className="mb-2">
          <Button
            onClick={handleDeleteSelected}
            disabled={selectedSegmentIds.length === 0}
            className={`${selectedSegmentIds.length === 0
              ? 'cursor-not-allowed bg-gray-300 text-gray-500'
              : 'bg-red-500 text-white hover:bg-red-600'
              }`}
          >
            Delete
          </Button>
        </div>
      </ShowFor>
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-200 bg-white">
          <thead>
            <tr>
              <th className="border-b px-4 py-2">
                <input
                  type="checkbox"
                  onChange={handleSelectAllChange}
                  checked={allSelected}
                />
              </th>
              <th className="border-b px-4 py-2 text-left text-sm font-medium text-gray-700">
                Name
              </th>
              <th className="border-b px-4 py-2 text-left text-sm font-medium text-gray-700">
                Created By
              </th>
              <th className="border-b px-4 py-2 text-left text-sm font-medium text-gray-700">
                Date Created
              </th>
              <th className="border-b px-4 py-2 text-left text-sm font-medium text-gray-700">
                Count
              </th>
              <ShowFor permissions={['SEGMENTS_WRITE']}>
                <th className="border-b px-4 py-2 text-left text-sm font-medium text-gray-700">
                  Actions
                </th>
              </ShowFor>
            </tr>
          </thead>
          <tbody>
            {!segmentsLoaded ? (
              <LoadingSpinner />
            ) : (
              segmentArray.map((segment) => {
                const isEditing = editingData.id === segment.id;
                return (
                  <SegmentListRow
                    key={segment.id}
                    segment={segment}
                    isEditing={isEditing}
                    editingValues={isEditing ? editingData : {}}
                    handleCellClick={handleCellClick}
                    handleInputChange={handleInputChange}
                    handleSaveClick={handleSaveClick}
                    handleCancelClick={handleCancelClick}
                    handleEditClick={handleEditClick}
                    isSelected={isSelected}
                    handleCheckboxChange={handleCheckboxChange}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Modal className={'max-w-xl'}>
        <div className="text-center">
          <p className="mb-4 text-lg">
            Are you sure you want to delete {selectedSegmentIds.length} selected
            item(s)?
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={confirmDelete}
              className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
            >
              Confirm
            </button>
            <button
              onClick={cancelDelete}
              className="rounded bg-gray-300 px-4 py-2 text-gray-800 hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SegmentList;
